// Chaves de ProvedorEndpoint.config lidas pelo runtime (ver §2.5/§2.8 da análise):
//   tokenName / tokenPath: endpoint de LOGIN (guarda o token do login sob tokenName, lido da resposta em tokenPath)
//   TOKEN: endpoint que CONSOME o token (Authorization: Bearer <valor>) quando requerAutenticacao = true

export type InfoLogin = { tokenName: string | null; tokenPath: string | null; contentType: string | null }

// Estrutural: aceita ProvedorEndpoint e objetos parciais (o método é um enum no tipo do endpoint)
export type EndpointBasico = { id?: string | null; nome?: string; requerAutenticacao?: boolean; metodo?: string; path?: string; config?: Record<string, any> | null }

const texto = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

export function infoLogin(endpoint: EndpointBasico | null | undefined): InfoLogin {
  const c = endpoint?.config ?? {};
  return { tokenName: texto(c['tokenName']), tokenPath: texto(c['tokenPath']), contentType: texto(c['ContentType']) };
}

/** O login só guarda o token se o endpoint tem os dois: onde ele vai na resposta e com que nome */
export function loginCompleto(endpoint: EndpointBasico | null | undefined): boolean {
  const i = infoLogin(endpoint);
  return !!i.tokenName && !!i.tokenPath;
}

/** Nome do token que o endpoint consome, ou null (só vale quando requerAutenticacao não é false) */
export function tokenConsumido(endpoint: EndpointBasico | null | undefined): string | null {
  if (!endpoint || endpoint.requerAutenticacao === false) return null;
  return texto(endpoint.config?.['TOKEN']);
}

/** Rótulo do endpoint no dropdown de login: destaca o que já é de login */
export function rotuloEndpointLogin(e: EndpointBasico): string {
  const i = infoLogin(e);
  const base = `${e.nome ?? ''} — ${e.metodo ?? ''} ${e.path ?? ''}`.trim();
  return i.tokenName ? `${base} · token ${i.tokenName}` : base;
}

/** Endpoints que já são de login primeiro, os demais depois */
export function ordenarEndpointsParaLogin<T extends EndpointBasico>(endpoints: T[]): T[] {
  return [...endpoints].sort((a, b) => Number(loginCompleto(b)) - Number(loginCompleto(a)));
}

export type RecursoPipeline = { id: number; nome: string; tipoRecurso: string; ordemExecucao: number; recursoPaiId?: number | null }
export type ComandoPipeline = { recursoId: number | null; endpointId?: string | null }

/**
 * Problemas do pipeline com tokens: quem consome um token que nenhum recurso de Autenticação anterior gera
 * (o runtime falha com NPE) e logins cujo endpoint não guarda token. Só olha recursos com comando de API.
 */
export function problemasDeTokens(recursos: RecursoPipeline[], comandos: ComandoPipeline[], endpoints: EndpointBasico[]): { recursoId: number; texto: string }[] {
  const porId = new Map(endpoints.filter(e => !!e.id).map(e => [e.id as string, e]));
  const endpointDoRecurso = (r: RecursoPipeline) => {
    const c = comandos.find(x => x.recursoId === r.id && !!x.endpointId);
    return c?.endpointId ? porId.get(c.endpointId) : undefined;
  };
  // sub-recursos (ITERACAO) rodam na posição do pai
  const posicao = (r: RecursoPipeline) => (r.recursoPaiId ? recursos.find(x => x.id === r.recursoPaiId)?.ordemExecucao : undefined) ?? r.ordemExecucao;

  const logins = recursos.filter(r => r.tipoRecurso === 'AUTENTICACAO');
  const problemas: { recursoId: number; texto: string }[] = [];

  for (const l of logins) {
    const e = endpointDoRecurso(l);
    if (e && !loginCompleto(e)) {
      problemas.push({ recursoId: l.id, texto: `O recurso de Autenticação "${l.nome}" usa o endpoint "${e.nome}", que não tem tokenName e tokenPath: o login não guardará nenhum token.` });
    }
  }

  for (const r of recursos.filter(x => x.tipoRecurso !== 'AUTENTICACAO')) {
    const e = endpointDoRecurso(r);
    const token = tokenConsumido(e);
    if (!token) continue;
    const gera = logins.some(l => posicao(l) < posicao(r) && infoLogin(endpointDoRecurso(l)).tokenName === token);
    if (!gera) {
      problemas.push({ recursoId: r.id, texto: `O recurso "${r.nome}" (ordem ${posicao(r)}) consome o token "${token}", mas nenhum recurso de Autenticação anterior o gera.` });
    }
  }
  return problemas;
}

export function validarTokensDoPipeline(recursos: RecursoPipeline[], comandos: ComandoPipeline[], endpoints: EndpointBasico[]): string[] {
  return problemasDeTokens(recursos, comandos, endpoints).map(p => p.texto);
}
